import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReportingPageLayout from './ReportingPageLayout';
import KpiCardGrid from './KpiCardGrid';
import TeamPagination from '../../TeamManager/components/TeamPagination';
import TableSettings from '../../shared/components/TableSettings';
import useReportingPage from '../hooks/useReportingPage';
import { getGranularity } from '../utils/reportingUtils';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import TrendBadge from './TrendBadge';
import MarketingReportCards from './MarketingReportCards';

const axios = require('axios').default;

const MARKETING_COLUMNS = [
  { id: 'created_at', label: 'Date', sortable: true, sortType: 'date', align: 'left', first: true, alwaysVisible: true },
  { id: 'name', label: 'Name', sortable: true, sortType: 'text', align: 'left', alwaysVisible: true },
  { id: 'source', label: 'Source', sortable: true, sortType: 'text', align: 'left' },
  { id: 'type', label: 'Channel', sortable: true, sortType: 'text', align: 'left' },
  { id: 'venue_name', label: 'Venue', sortable: true, sortType: 'text', align: 'left' },
  { id: 'subject', label: 'Subject', sortable: true, sortType: 'text', align: 'left', defaultHidden: true },
  { id: 'sent', label: 'Sent', sortable: true, sortType: 'number', align: 'right' },
  { id: 'delivered', label: 'Delivered', sortable: true, sortType: 'number', align: 'right' },
  { id: 'opened', label: 'Opened', sortable: true, sortType: 'number', align: 'right' },
  { id: 'open_rate', label: 'Open Rate', sortable: true, sortType: 'number', align: 'right' },
  { id: 'clicked', label: 'Clicked', sortable: true, sortType: 'number', align: 'right' },
  { id: 'click_rate', label: 'Click Rate', sortable: true, sortType: 'number', align: 'right', defaultHidden: true },
  { id: 'conversions', label: 'Conversions', sortable: true, sortType: 'number', align: 'right' },
];

const getDefaultVisibleColumns = () => MARKETING_COLUMNS.filter(c => !c.defaultHidden).map(c => ({ id: c.id, label: c.label, alwaysVisible: c.alwaysVisible }));
const getDefaultHiddenColumns = () => MARKETING_COLUMNS.filter(c => c.defaultHidden).map(c => ({ id: c.id, label: c.label }));

const SORT_TYPES = {
  text: { asc: 'A \u2192 Z', desc: 'Z \u2192 A' },
  number: { asc: 'Low \u2192 High', desc: 'High \u2192 Low' },
  date: { asc: 'Oldest first', desc: 'Newest first' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '\u2014';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

function ReportingMarketingPage({ team, csrfToken, currentUser, venueOwnerships }) {
  const {
    isMobile, sidebarOpen, setSidebarOpen,
    dateRange, setDateRange,
    filterSidebarOpen, setFilterSidebarOpen,
    activeFilters, comparison,
    handleFilterChange, handleRemoveFilter, handleClearAllFilters,
  } = useReportingPage('year_to_date');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [sortRules, setSortRules] = useState([{ field: 'created_at', direction: 'desc' }]);
  const [sortPopoverOpen, setSortPopoverOpen] = useState(false);
  const [tableSettingsOpen, setTableSettingsOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(getDefaultVisibleColumns());
  const [hiddenColumns, setHiddenColumns] = useState(getDefaultHiddenColumns());
  const [page, setPage] = useState(1);
  const perPage = 25;

  const filterGroups = useMemo(() => {
    const groups = [];
    if (venueOwnerships && venueOwnerships.length > 0) {
      groups.push({
        label: 'Venues',
        filters: [
          {
            id: 'venue_ownership_id',
            label: 'Venue',
            type: 'checkbox',
            options: venueOwnerships.map((vo) => ({
              value: String(vo.id),
              label: vo.name || vo.venue_name || `Venue ${vo.id}`,
            })),
          },
        ],
      });
    }
    groups.push({
      label: 'Source',
      filters: [
        {
          id: 'source',
          label: 'Source',
          type: 'checkbox',
          options: [
            { value: 'Broadcast', label: 'Broadcast' },
            { value: 'Campaign', label: 'Campaign' },
            { value: 'Workflow', label: 'Workflow' },
          ],
        },
      ],
    });
    groups.push({
      label: 'Channel',
      filters: [
        {
          id: 'channel',
          label: 'Channel',
          type: 'checkbox',
          options: [
            { value: 'Email', label: 'Email' },
            { value: 'SMS', label: 'SMS' },
          ],
        },
      ],
    });
    return groups;
  }, [venueOwnerships]);

  useEffect(() => {
    if (!team || !team.id) return;
    const saved = localStorage.getItem(`reportingMarketingColumns_${team.id}`);
    if (saved) {
      try {
        const { visible, hidden } = JSON.parse(saved);
        const knownIds = new Set(MARKETING_COLUMNS.map(c => c.id));
        const validVisible = visible.filter(c => knownIds.has(c.id));
        const validHidden = hidden.filter(c => knownIds.has(c.id));
        const savedIds = new Set([...validVisible.map(c => c.id), ...validHidden.map(c => c.id)]);
        const newColumns = MARKETING_COLUMNS.filter(c => !savedIds.has(c.id) && !c.alwaysVisible)
          .map(c => ({ id: c.id, label: c.label, alwaysVisible: c.alwaysVisible }));
        setVisibleColumns(validVisible);
        setHiddenColumns([...validHidden, ...newColumns]);
      } catch (e) {}
    }
  }, [team && team.id]);

  const handleColumnsChange = (newVisible, newHidden) => {
    setVisibleColumns(newVisible);
    setHiddenColumns(newHidden);
    localStorage.setItem(`reportingMarketingColumns_${team.id}`, JSON.stringify({
      visible: newVisible,
      hidden: newHidden,
    }));
  };

  const buildApiParams = useCallback(() => {
    const params = {};
    if (dateRange && dateRange.start) params.start_date = dateRange.start;
    if (dateRange && dateRange.end) params.end_date = dateRange.end;
    params.granularity = getGranularity(dateRange);
    if (comparison.start) params.prior_start_date = comparison.start;
    if (comparison.end) params.prior_end_date = comparison.end;
    if (activeFilters.venue_ownership_id && activeFilters.venue_ownership_id.length > 0) {
      params['venue_ownership_id[]'] = activeFilters.venue_ownership_id;
    }
    if (activeFilters.source && activeFilters.source.length > 0) {
      params['source[]'] = activeFilters.source;
    }
    if (activeFilters.channel && activeFilters.channel.length > 0) {
      params['channel[]'] = activeFilters.channel;
    }
    return params;
  }, [dateRange, activeFilters, comparison]);

  useEffect(() => {
    if (!team || !team.id) return;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    setLoading(true);
    const params = buildApiParams();
    axios.get(`/teams/${team.id}/reporting/marketing/data.json`, { params })
      .then((res) => setData(res.data || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [team && team.id, csrfToken, dateRange, activeFilters]);

  const kpis = (data && data.kpis) || {};
  const prior = kpis.prior || {};
  const subscribers = (data && data.subscribers) || {};
  const messages = (data && data.messages) || {};
  const rows = messages.rows || [];
  const totals = messages.totals || {};
  const cmpLabel = comparison.label;

  const sortedRows = useMemo(() => {
    const sorted = [...rows];
    sorted.sort((a, b) => {
      for (const rule of sortRules) {
        let av = a[rule.field];
        let bv = b[rule.field];
        if (rule.field === 'created_at') {
          av = av ? new Date(av).getTime() : 0;
          bv = bv ? new Date(bv).getTime() : 0;
        }
        if (typeof av === 'string') av = av.toLowerCase();
        if (typeof bv === 'string') bv = bv.toLowerCase();
        if (av == null) av = 0;
        if (bv == null) bv = 0;
        if (av < bv) return rule.direction === 'desc' ? 1 : -1;
        if (av > bv) return rule.direction === 'desc' ? -1 : 1;
      }
      return 0;
    });
    return sorted;
  }, [rows, sortRules]);

  const filteredRows = useMemo(() => {
    if (!search) return sortedRows;
    const q = search.toLowerCase();
    return sortedRows.filter((r) =>
      (r.name || '').toLowerCase().includes(q) ||
      (r.subject || '').toLowerCase().includes(q) ||
      (r.venue_name || '').toLowerCase().includes(q) ||
      (r.source || '').toLowerCase().includes(q) ||
      (r.type || '').toLowerCase().includes(q) ||
      String(r.id).includes(q)
    );
  }, [sortedRows, search]);

  useEffect(() => { setPage(1); }, [search, sortRules, rows]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / perPage));
  const paginatedRows = filteredRows.slice((page - 1) * perPage, page * perPage);

  const sortableColumns = MARKETING_COLUMNS.filter(c => c.sortable);

  const handleColumnSort = (colId) => {
    setSortRules(prev => {
      const existing = prev.length === 1 && prev[0].field === colId;
      if (existing) {
        return [{ field: colId, direction: prev[0].direction === 'asc' ? 'desc' : 'asc' }];
      }
      return [{ field: colId, direction: 'asc' }];
    });
  };

  const handleSortRuleChange = (index, key, value) => {
    setSortRules(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [key]: value };
      return updated;
    });
  };

  const handleAddSortRule = () => {
    const usedFields = new Set(sortRules.map(r => r.field));
    const nextCol = sortableColumns.find(c => !usedFields.has(c.id));
    if (nextCol) {
      setSortRules(prev => [...prev, { field: nextCol.id, direction: 'asc' }]);
    }
  };

  const handleRemoveSortRule = (index) => {
    setSortRules(prev => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const sortArrow = (colId) => {
    const rule = sortRules.find(r => r.field === colId);
    if (!rule) return null;
    const index = sortRules.indexOf(rule);
    const arrow = rule.direction === 'asc' ? '\u2191' : '\u2193';
    return sortRules.length > 1 ? ` ${index + 1}${arrow}` : ` ${arrow}`;
  };

  useEffect(() => {
    if (!sortPopoverOpen) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.reporting-sort-popover')) {
        setSortPopoverOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [sortPopoverOpen]);

  const compactNumber = (n) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toLocaleString();
  };

  const renderCellValue = (colId, row) => {
    switch (colId) {
      case 'created_at':
        return formatDate(row.created_at);
      case 'name':
        return <div style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.name || '\u2014'}</div>;
      case 'source': {
        const sourceColors = {
          Broadcast: { background: '#f9f5ff', color: '#6941c6', border: '1px solid #d6bbfb' },
          Campaign: { background: 'var(--sl-bg-error-primary, #fef3f2)', color: 'var(--sl-text-error, #b42318)', border: '1px solid var(--sl-border-error, #fecdca)' },
          Workflow: { background: 'var(--sl-bg-warning-primary, #fffaeb)', color: 'var(--sl-text-warning, #b54708)', border: '1px solid #fedf89' },
        };
        const style = sourceColors[row.source] || { background: 'var(--sl-bg-tertiary, #f2f4f7)', color: 'var(--sl-text-secondary, #344054)', border: '1px solid var(--sl-border-secondary, #e4e7ec)' };
        return <span className="badge" style={{ ...style, fontSize: 12, fontWeight: 500, padding: '2px 8px', borderRadius: 12 }}>{row.source}</span>;
      }
      case 'type': {
        const channelColors = {
          SMS: { background: 'var(--sl-bg-success-primary, #ecfdf3)', color: 'var(--sl-text-success, #027a48)', border: '1px solid #a6f4c5' },
          Email: { background: 'var(--sl-bg-brand-primary, #eff8ff)', color: 'var(--sl-fg-brand, #175cd3)', border: '1px solid var(--sl-border-brand, #b2ddff)' },
        };
        const channels = row.type === 'Email & SMS' ? ['Email', 'SMS'] : [row.type];
        return (
          <div className="d-flex" style={{ gap: 4 }}>
            {channels.map((ch) => (
              <span key={ch} className="badge" style={{ ...(channelColors[ch] || channelColors.Email), fontSize: 12, fontWeight: 500, padding: '2px 8px', borderRadius: 12 }}>{ch}</span>
            ))}
          </div>
        );
      }
      case 'venue_name':
        return <div style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.venue_name || '\u2014'}</div>;
      case 'subject':
        return <div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.subject || '\u2014'}</div>;
      case 'sent':
        return (row.sent || 0).toLocaleString();
      case 'delivered':
        return (row.delivered || 0).toLocaleString();
      case 'opened':
        return (row.opened || 0).toLocaleString();
      case 'open_rate':
        return row.open_rate != null ? `${row.open_rate}%` : '\u2014';
      case 'clicked':
        return (row.clicked || 0).toLocaleString();
      case 'click_rate':
        return row.click_rate != null ? `${row.click_rate}%` : '\u2014';
      case 'conversions':
        return (row.conversions || 0).toLocaleString();
      default:
        return '\u2014';
    }
  };

  const kpiCards = [
    {
      top: { label: 'Emails Sent', value: compactNumber(kpis.emails_sent || 0), current: kpis.emails_sent, prior: prior.emails_sent, tooltip: 'Total emails delivered in the period' },
      bottom: { label: 'Email Click Rate', value: `${kpis.click_rate || 0}%`, current: kpis.click_rate, prior: prior.click_rate, tooltip: 'Percentage of delivered emails that were clicked' },
    },
    {
      top: { label: 'SMS Sent', value: compactNumber(kpis.sms_sent || 0), current: kpis.sms_sent, prior: prior.sms_sent, tooltip: 'Total SMS messages sent in the period' },
      bottom: { label: 'SMS Clicked', value: compactNumber(kpis.sms_clicked || 0), current: kpis.sms_clicked, prior: prior.sms_clicked, tooltip: 'Total SMS messages clicked in the period' },
    },
    {
      top: { label: 'Total Fans', value: compactNumber(kpis.total_fans || 0), current: kpis.total_fans, prior: prior.total_fans, tooltip: 'Total number of fans across all time' },
      bottom: { label: 'Total New Fans', value: compactNumber(kpis.new_fans || 0), current: kpis.new_fans, prior: prior.new_fans, tooltip: 'New fans created during the selected period' },
    },
  ];

  return (
    <ReportingPageLayout
      team={team}
      activeKey="marketing"
      title="Marketing"
      subtitle="Overview of your email, SMS, and marketing performance."
      dateRange={dateRange}
      setDateRange={setDateRange}
      isMobile={isMobile}
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      filterSidebarOpen={filterSidebarOpen}
      setFilterSidebarOpen={setFilterSidebarOpen}
      activeFilters={activeFilters}
      filterGroups={filterGroups}
      handleFilterChange={handleFilterChange}
      handleRemoveFilter={handleRemoveFilter}
      handleClearAllFilters={handleClearAllFilters}
      filterTitle="Filter Marketing"
      filterSubtitle="Apply filters to narrow marketing data."
    >
          <MarketingReportCards team={team} isMobile={isMobile} />

          <KpiCardGrid
            loading={loading}
            isMobile={isMobile}
            columns={3}
            cards={kpiCards}
            comparisonLabel={cmpLabel}
            variant="centered"
          />

          {loading ? (
            <div style={{ background: 'var(--sl-bg-primary, #fff)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8, padding: '20px 24px', marginBottom: 24 }}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ width: 160, height: 18, background: 'var(--sl-bg-tertiary, #f2f4f7)', borderRadius: 4 }} />
              </div>
              <div style={{ width: 120, height: 36, background: 'var(--sl-bg-tertiary, #f2f4f7)', borderRadius: 4, marginBottom: 8 }} />
              <div style={{ width: 180, height: 16, background: 'var(--sl-bg-tertiary, #f2f4f7)', borderRadius: 4, marginBottom: 20 }} />
              <div style={{ width: '100%', height: 180, background: 'var(--sl-bg-secondary, #f9fafb)', borderRadius: 4 }} />
            </div>
          ) : (
            <div style={{ background: 'var(--sl-bg-primary, #fff)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8, padding: '20px 24px', marginBottom: 24 }}>
              <div className="d-flex align-items-center" style={{ marginBottom: 16, gap: 8 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--sl-text-primary, #101828)' }}>Total Subscribers</span>
                <span className="badge" style={{ background: 'var(--sl-bg-secondary, #f9fafb)', color: 'var(--sl-text-tertiary, #667085)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 12 }}>
                  <i className="far fa-clock mr-1" style={{ fontSize: 10 }} />
                  Delayed by 15 minutes
                </span>
              </div>
              <div style={{ fontSize: 30, fontWeight: 700, color: 'var(--sl-text-primary, #101828)', lineHeight: 1.2, marginBottom: 8 }}>
                {(subscribers.total || 0).toLocaleString()}
              </div>
              <div className="d-flex align-items-center" style={{ gap: 8, marginBottom: 20 }}>
                <TrendBadge current={subscribers.total} prior={subscribers.prior_total} label={cmpLabel} />
                {(subscribers.new_in_period || 0) > 0 && (
                  <span style={{ fontSize: 13, color: 'var(--sl-text-tertiary, #667085)' }}>
                    + {(subscribers.new_in_period).toLocaleString()} new
                  </span>
                )}
              </div>
              {(subscribers.daily || []).length > 0 && (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={subscribers.daily} margin={{ top: 5, right: 0, left: 0, bottom: 5 }} barCategoryGap="10%">
                    <CartesianGrid vertical={false} stroke="#f2f4f7" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12, fill: 'var(--sl-text-tertiary, #667085)' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(d) => {
                        const date = new Date(d + 'T00:00:00');
                        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      }}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: 'var(--sl-text-tertiary, #667085)' }}
                      axisLine={false}
                      tickLine={false}
                      width={40}
                    />
                    <Tooltip
                      cursor={false}
                      content={({ active, payload, label }) => {
                        if (!active || !payload || !payload.length) return null;
                        const date = new Date(label + 'T00:00:00');
                        const dateLabel = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                        return (
                          <div style={{ background: '#000', borderRadius: 8, padding: 12, minWidth: 140 }}>
                            <p style={{ color: 'var(--sl-text-white, #fff)', fontSize: 12, margin: '0 0 8px 0' }}>{dateLabel}</p>
                            <div className="d-flex align-items-center" style={{ gap: 6 }}>
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#6A4C93', flexShrink: 0 }} />
                              <span style={{ color: '#a1a1aa', fontSize: 12 }}>Subscribers:</span>
                              <span style={{ color: 'var(--sl-text-white, #fff)', fontSize: 12, fontWeight: 600 }}>{payload[0].value.toLocaleString()}</span>
                            </div>
                          </div>
                        );
                      }}
                    />
                    <defs>
                      <linearGradient id="subscriberBarGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#6A4C93" />
                        <stop offset="100%" stopColor="#B8A9D4" />
                      </linearGradient>
                    </defs>
                    <Bar dataKey="count" fill="url(#subscriberBarGradient)" radius={[4, 4, 0, 0]} barSize={40} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          )}

          <div style={{ background: 'var(--sl-bg-primary, #fff)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8 }}>
            <div className="d-flex align-items-center justify-content-between" style={{ padding: '16px 24px', borderBottom: '1px solid var(--sl-bg-tertiary, #f2f4f7)' }}>
              <div className="d-flex align-items-center" style={{ gap: 8 }}>
                <h6 style={{ fontWeight: 700, fontSize: 16, margin: 0, color: 'var(--sl-text-primary, #101828)' }}>Messages</h6>
                <span className="badge" style={{ background: 'var(--sl-bg-secondary, #f9fafb)', color: 'var(--sl-text-tertiary, #667085)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 12 }}>
                  <i className="far fa-clock mr-1" style={{ fontSize: 10 }} />
                  Delayed by 15 minutes
                </span>
              </div>
              <div className="d-flex align-items-center" style={{ gap: 8 }}>
                <button
                  className="btn btn-sm d-flex align-items-center"
                  style={{ gap: 6, fontSize: 13, fontWeight: 500, padding: '4px 12px', borderRadius: 6, border: '1px solid var(--sl-border-primary, #d0d5dd)', background: 'var(--sl-bg-primary, #fff)', color: 'var(--sl-text-secondary, #344054)', height: 32 }}
                  onClick={() => {
                    const header = visibleColumns.map(vc => {
                      const col = MARKETING_COLUMNS.find(c => c.id === vc.id);
                      return col ? col.label : vc.id;
                    });
                    const csvRows = sortedRows.map((r) => visibleColumns.map(vc => {
                      switch (vc.id) {
                        case 'created_at': return r.created_at ? new Date(r.created_at).toLocaleDateString() : '';
                        case 'name': return r.name || '';
                        case 'source': return r.source || '';
                        case 'type': return r.type || '';
                        case 'venue_name': return r.venue_name || '';
                        case 'subject': return r.subject || '';
                        case 'sent': return r.sent || 0;
                        case 'delivered': return r.delivered || 0;
                        case 'opened': return r.opened || 0;
                        case 'open_rate': return r.open_rate != null ? r.open_rate : '';
                        case 'clicked': return r.clicked || 0;
                        case 'click_rate': return r.click_rate != null ? r.click_rate : '';
                        case 'conversions': return r.conversions || 0;
                        default: return '';
                      }
                    }));
                    const csv = [header, ...csvRows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'marketing_messages.csv';
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  Export CSV <i className="fas fa-download" style={{ fontSize: 12 }} />
                </button>
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    className="btn btn-sm d-flex align-items-center"
                    onClick={() => setTableSettingsOpen(!tableSettingsOpen)}
                    style={{ fontSize: 13, fontWeight: 500, padding: '4px 12px', borderRadius: 6, border: '1px solid var(--sl-border-primary, #d0d5dd)', background: 'var(--sl-bg-primary, #fff)', color: 'var(--sl-text-secondary, #344054)', height: 32 }}
                  >
                    <i className="fas fa-columns mr-2" style={{ fontSize: 12 }} />
                    Table Settings
                    {hiddenColumns.length > 0 && (
                      <span className="ml-1" style={{ background: 'var(--sl-fg-brand, #1982c4)', color: 'var(--sl-text-white, #fff)', borderRadius: 10, padding: '0 6px', fontSize: 11, fontWeight: 600, lineHeight: '18px' }}>
                        {hiddenColumns.length}
                      </span>
                    )}
                  </button>
                  <TableSettings
                    isOpen={tableSettingsOpen}
                    onClose={() => setTableSettingsOpen(false)}
                    visibleFields={visibleColumns}
                    hiddenFields={hiddenColumns}
                    onFieldsChange={handleColumnsChange}
                  />
                </div>
                <div className="reporting-sort-popover" style={{ position: 'relative' }}>
                  <button
                    type="button"
                    className="btn btn-sm d-flex align-items-center"
                    onClick={() => setSortPopoverOpen(!sortPopoverOpen)}
                    style={{ fontSize: 13, fontWeight: 500, padding: '4px 12px', borderRadius: 6, border: '1px solid var(--sl-border-primary, #d0d5dd)', background: 'var(--sl-bg-primary, #fff)', color: 'var(--sl-text-secondary, #344054)', height: 32 }}
                  >
                    <i className="fas fa-sort mr-2" style={{ fontSize: 12 }} />
                    Sort{sortRules.length > 1 ? ` (${sortRules.length})` : ''}
                  </button>
                  {sortPopoverOpen && (
                    <div
                      className="card shadow-sm"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        left: 'auto',
                        zIndex: 1050,
                        minWidth: isMobile ? 280 : 420,
                        marginTop: 4,
                      }}
                    >
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <span className="font-weight-bold">Sort by</span>
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-muted"
                            onClick={() => setSortPopoverOpen(false)}
                          >
                            <i className="fal fa-times" />
                          </button>
                        </div>
                        {sortRules.map((rule, index) => {
                          const col = MARKETING_COLUMNS.find(c => c.id === rule.field);
                          const sortType = col?.sortType || 'text';
                          const dirLabels = SORT_TYPES[sortType] || SORT_TYPES.text;
                          return (
                            <div key={index} className="d-flex align-items-center gap-2 mb-2">
                              <select
                                className="form-control form-control-sm"
                                style={{ flex: '1 1 0' }}
                                value={rule.field}
                                onChange={(e) => handleSortRuleChange(index, 'field', e.target.value)}
                              >
                                {sortableColumns.map(c => (
                                  <option
                                    key={c.id}
                                    value={c.id}
                                    disabled={sortRules.some((r, i) => i !== index && r.field === c.id)}
                                  >
                                    {c.label}
                                  </option>
                                ))}
                              </select>
                              <select
                                className="form-control form-control-sm"
                                style={{ flex: '1 1 0' }}
                                value={rule.direction}
                                onChange={(e) => handleSortRuleChange(index, 'direction', e.target.value)}
                              >
                                <option value="asc">{dirLabels.asc}</option>
                                <option value="desc">{dirLabels.desc}</option>
                              </select>
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 text-muted"
                                onClick={() => handleRemoveSortRule(index)}
                                disabled={sortRules.length <= 1}
                                style={{ opacity: sortRules.length <= 1 ? 0.3 : 1 }}
                              >
                                <i className="fal fa-times" />
                              </button>
                            </div>
                          );
                        })}
                        {sortRules.length < sortableColumns.length && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-primary"
                            onClick={handleAddSortRule}
                          >
                            <i className="fal fa-plus mr-1" />
                            Add another sort
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--sl-bg-tertiary, #f2f4f7)' }}>
              <div style={{ position: 'relative', maxWidth: 400 }}>
                <i className="fas fa-search" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--sl-text-disabled, #98a2b3)', fontSize: 14 }} />
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search messages..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ paddingLeft: 36, border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8, height: 40 }}
                />
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr>
                    {visibleColumns.map((vc) => {
                      const col = MARKETING_COLUMNS.find(c => c.id === vc.id);
                      if (!col) return null;
                      return (
                        <th
                          key={col.id}
                          style={{
                            fontWeight: 500,
                            fontSize: 12,
                            color: 'var(--sl-text-tertiary, #667085)',
                            textTransform: 'uppercase',
                            textAlign: col.align,
                            ...(col.sortable ? { cursor: 'pointer', userSelect: 'none' } : {}),
                            whiteSpace: 'nowrap',
                            ...(col.first ? { paddingLeft: 24 } : {}),
                          }}
                          onClick={col.sortable ? () => handleColumnSort(col.id) : undefined}
                        >
                          {col.label}{sortArrow(col.id)}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={visibleColumns.length} className="text-center py-4 text-muted">Loading...</td>
                    </tr>
                  ) : paginatedRows.length === 0 ? (
                    <tr>
                      <td colSpan={visibleColumns.length} className="text-center py-4 text-muted">{search ? 'No messages match your search.' : 'No message data for the selected period.'}</td>
                    </tr>
                  ) : paginatedRows.map((row) => (
                    <tr key={row.id}>
                      {visibleColumns.map((vc) => {
                        const col = MARKETING_COLUMNS.find(c => c.id === vc.id);
                        if (!col) return null;
                        return (
                          <td key={col.id} style={{
                            fontSize: 14,
                            color: col.id === 'name' ? 'var(--sl-text-primary, #101828)' : 'var(--sl-text-secondary, #344054)',
                            fontWeight: col.id === 'name' ? 600 : undefined,
                            textAlign: col.align,
                            whiteSpace: col.id === 'created_at' ? 'nowrap' : undefined,
                            ...(col.first ? { paddingLeft: 24 } : {}),
                          }}>
                            {renderCellValue(col.id, row)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
                {filteredRows.length > 0 && (
                  <tfoot>
                    <tr style={{ borderTop: '2px solid var(--sl-border-secondary, #e4e7ec)' }}>
                      {visibleColumns.map((vc, i) => {
                        const col = MARKETING_COLUMNS.find(c => c.id === vc.id);
                        if (!col) return null;
                        const totalMap = {
                          sent: (totals.sent || 0).toLocaleString(),
                          delivered: (totals.delivered || 0).toLocaleString(),
                          opened: (totals.opened || 0).toLocaleString(),
                          open_rate: `${totals.open_rate || 0}%`,
                          clicked: (totals.clicked || 0).toLocaleString(),
                          click_rate: `${totals.click_rate || 0}%`,
                          conversions: (totals.conversions || 0).toLocaleString(),
                        };
                        if (totalMap[col.id]) {
                          return (
                            <td key={col.id} style={{ textAlign: 'right', fontWeight: 700, color: 'var(--sl-text-primary, #101828)' }}>
                              {totalMap[col.id]}
                            </td>
                          );
                        }
                        const isLastNonTotal = !visibleColumns.slice(i + 1).some(v => totalMap[v.id]);
                        if (isLastNonTotal) {
                          return (
                            <td key={col.id} colSpan={1} style={{ paddingLeft: col.first ? 24 : undefined, fontWeight: 700, color: 'var(--sl-text-primary, #101828)', textAlign: 'right' }}>
                              Grand total
                            </td>
                          );
                        }
                        return <td key={col.id} />;
                      })}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            {totalPages > 1 && (
              <div style={{ padding: '12px 24px', borderTop: '1px solid var(--sl-bg-tertiary, #f2f4f7)' }}>
                <TeamPagination
                  currentPage={page}
                  totalPages={totalPages}
                  totalItems={filteredRows.length}
                  pageSize={perPage}
                  itemLabel="messages"
                  onPageChange={(p) => {
                    if (p >= 1 && p <= totalPages) setPage(p);
                  }}
                />
              </div>
            )}
          </div>

    </ReportingPageLayout>
  );
}

export default ReportingMarketingPage;
